import React, { useEffect, useMemo, useRef, useState } from 'react';
import Map, { Layer, Marker, Source } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { resolveAvatarUrl, getOnlineStatus } from '../../utils/locationUtils';
import LocationName from './LocationName';
import { getMapLibreSatelliteStyle, getMapLibreStyle } from './mapProviders';
import './EmployeeMapAdvanced.css';

const STATUS_DOT_COLOR = {
  moving: '#3b82f6',
  'in-office': '#14b8a6',
  field: '#f59e0b',
  'checked-out': '#94a3b8',
  offline: '#fb7185',
  'no-location': '#cbd5e1',
};

const ProfilePin = ({ employee, isSelected, onClick }) => {
  const avatar = resolveAvatarUrl(employee.profilePic, employee.name);
  const online = getOnlineStatus(employee);
  const dotColor = online.isOnline ? '#22c55e' : STATUS_DOT_COLOR[employee.status] || STATUS_DOT_COLOR.offline;

  return (
    <Marker
      longitude={employee.longitude}
      latitude={employee.latitude}
      anchor="bottom"
      onClick={(event) => {
        event.originalEvent.stopPropagation();
        onClick?.(employee.userId);
      }}
    >
      <div
        className={`advanced-map-marker ${isSelected ? 'is-selected' : ''} ${online.isOnline ? 'is-online' : 'is-offline'} ${employee.isApproximateLocation ? 'is-approximate' : ''}`}
      >
        <img src={avatar} alt={employee.name} title={employee.name} />
        <span className="advanced-map-marker-tooltip">
          {employee.name} · {online.label}
          <br />
          <LocationName
            lat={employee.latitude}
            lng={employee.longitude}
            approximate={employee.isApproximateLocation}
            accuracy={employee.accuracy}
            compact
            showCoords={false}
          />
        </span>
        <span className="advanced-map-marker-dot" style={{ background: dotColor }} />
      </div>
    </Marker>
  );
};

function EmployeeMapAdvanced({
  employees = [],
  offices = [],
  selectedId,
  onSelectEmployee,
  center,
  resizeToken = 0,
  fullView = false,
  searchPlace = null,
  fitRequestToken = 0,
}) {
  const mapRef = useRef(null);
  const lastSelectedIdRef = useRef(null);
  const userLockedRef = useRef(false);
  const initialFitDoneRef = useRef(false);
  const lastFitTokenRef = useRef(0);
  const [styleMode, setStyleMode] = useState('vector'); // vector | satellite
  const [mapStyle, setMapStyle] = useState(() => getMapLibreStyle());

  useEffect(() => {
    setMapStyle(styleMode === 'satellite' ? getMapLibreSatelliteStyle() : getMapLibreStyle());
  }, [styleMode]);

  useEffect(() => {
    const map = mapRef.current?.getMap?.();
    if (!map) return undefined;
    const timer = setTimeout(() => map.resize(), 120);
    return () => clearTimeout(timer);
  }, [resizeToken, styleMode]);

  useEffect(() => {
    const map = mapRef.current?.getMap?.();
    if (!map) return undefined;

    const lock = () => {
      userLockedRef.current = true;
    };
    map.on('dragstart', lock);
    map.on('zoomstart', lock);
    map.on('rotatestart', lock);
    map.on('pitchstart', lock);

    return () => {
      map.off('dragstart', lock);
      map.off('zoomstart', lock);
      map.off('rotatestart', lock);
      map.off('pitchstart', lock);
    };
  }, []);

  useEffect(() => {
    if (searchPlace?.lat == null || searchPlace?.lng == null) return;
    const map = mapRef.current?.getMap?.();
    if (!map) return;

    userLockedRef.current = true;
    map.flyTo({
      center: [searchPlace.lng, searchPlace.lat],
      zoom: Math.max(map.getZoom(), 15),
      pitch: 62,
      bearing: -28,
      duration: 1200,
      essential: true,
    });
  }, [searchPlace?.id, searchPlace?.lat, searchPlace?.lng]);

  const locatedEmployees = useMemo(
    () => employees.filter((emp) => emp.latitude != null && emp.longitude != null),
    [employees]
  );

  const selectedEmployee = useMemo(
    () => locatedEmployees.find((emp) => emp.userId === selectedId) || null,
    [locatedEmployees, selectedId]
  );

  useEffect(() => {
    const forceFit = fitRequestToken > 0 && fitRequestToken !== lastFitTokenRef.current;
    if (forceFit) {
      lastFitTokenRef.current = fitRequestToken;
      userLockedRef.current = false;
      initialFitDoneRef.current = false;
    }

    if (initialFitDoneRef.current || (userLockedRef.current && !forceFit) || (selectedId && !forceFit)) {
      return;
    }
    if (locatedEmployees.length === 0 && offices.length === 0) return;
    const map = mapRef.current?.getMap?.();
    if (!map) return;

    initialFitDoneRef.current = true;
    const points = [
      ...locatedEmployees.map((e) => [e.longitude, e.latitude]),
      ...offices.map((o) => [o.longitude, o.latitude]),
    ];

    if (points.length === 1) {
      map.flyTo({
        center: points[0],
        zoom: 17,
        pitch: 62,
        bearing: -28,
        duration: 900,
        essential: true,
      });
      return;
    }

    const lngs = points.map((p) => p[0]);
    const lats = points.map((p) => p[1]);
    map.fitBounds(
      [
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)],
      ],
      { padding: 70, maxZoom: 17, duration: 900, pitch: 55, bearing: -20 }
    );
  }, [locatedEmployees, offices, selectedId, fitRequestToken]);

  useEffect(() => {
    if (!selectedEmployee) {
      lastSelectedIdRef.current = null;
      return;
    }

    const map = mapRef.current?.getMap?.();
    if (!map) return;

    const isNewSelection = lastSelectedIdRef.current !== selectedEmployee.userId;
    lastSelectedIdRef.current = selectedEmployee.userId;
    if (!isNewSelection) return;

    userLockedRef.current = true;
    map.flyTo({
      center: [selectedEmployee.longitude, selectedEmployee.latitude],
      zoom: Math.max(map.getZoom(), 18),
      pitch: 65,
      bearing: map.getBearing(),
      duration: 900,
      essential: true,
    });
  }, [selectedEmployee]);

  const trailGeoJson = useMemo(() => {
    if (!selectedEmployee?.trail || selectedEmployee.trail.length < 2) {
      return { type: 'FeatureCollection', features: [] };
    }

    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: { name: selectedEmployee.name },
          geometry: {
            type: 'LineString',
            coordinates: selectedEmployee.trail.map((point) => [point.lng, point.lat]),
          },
        },
      ],
    };
  }, [selectedEmployee]);

  return (
    <div className={`advanced-map-shell ${fullView ? 'is-fullview' : ''}`}>
      <div className="advanced-map-badge-row">
        <div className="advanced-map-badge">3D · OpenFreeMap</div>
        <div className="advanced-map-style-toggle">
          <button
            type="button"
            className={styleMode === 'vector' ? 'is-active' : ''}
            onClick={() => setStyleMode('vector')}
          >
            Streets
          </button>
          <button
            type="button"
            className={styleMode === 'satellite' ? 'is-active' : ''}
            onClick={() => setStyleMode('satellite')}
          >
            Satellite
          </button>
        </div>
      </div>

      <Map
        key={styleMode}
        ref={mapRef}
        initialViewState={{
          longitude: center.lng,
          latitude: center.lat,
          zoom: 14,
          pitch: 62,
          bearing: -28,
        }}
        mapStyle={mapStyle}
        style={{ width: '100%', height: '100%' }}
        maxPitch={85}
        maxZoom={22}
        dragRotate
        touchPitch
        touchZoomRotate
        onError={() => {
          // If OpenFreeMap fails, fall back to satellite raster style.
          if (styleMode === 'vector') {
            setStyleMode('satellite');
          }
        }}
      >
        {trailGeoJson.features.length > 0 && (
          <Source id="trails" type="geojson" data={trailGeoJson}>
            <Layer
              id="trail-lines"
              type="line"
              paint={{
                'line-color': '#1a365d',
                'line-width': 4,
                'line-opacity': 0.85,
              }}
            />
          </Source>
        )}

        {offices.map((office) => (
          <Marker
            key={office.name}
            longitude={office.longitude}
            latitude={office.latitude}
            anchor="center"
          >
            <div className="advanced-map-office">{office.name.slice(0, 1)}</div>
          </Marker>
        ))}

        {locatedEmployees.map((employee) => (
          <ProfilePin
            key={employee.userId}
            employee={employee}
            isSelected={employee.userId === selectedId}
            onClick={onSelectEmployee}
          />
        ))}

        {searchPlace?.lat != null && searchPlace?.lng != null ? (
          <Marker longitude={searchPlace.lng} latitude={searchPlace.lat} anchor="bottom">
            <div className="search-map-marker-advanced" title={searchPlace.label} />
          </Marker>
        ) : null}
      </Map>
    </div>
  );
}

export default EmployeeMapAdvanced;
